import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaWhatsapp } from 'react-icons/fa';
import emailjs from '@emailjs/browser';
import './styles/Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  // CAPTCHA state
  const [captcha, setCaptcha] = useState({ num1: 0, num2: 0, answer: 0 });
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaError, setCaptchaError] = useState('');
  
  // Form submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ type: '', text: '' });

  // Generate new CAPTCHA
  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setCaptcha({ num1, num2, answer: num1 + num2 });
    setCaptchaInput('');
    setCaptchaError('');
  };

  // Generate CAPTCHA on component mount
  useEffect(() => {
    generateCaptcha();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCaptchaChange = (e) => {
    setCaptchaInput(e.target.value);
    setCaptchaError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate CAPTCHA
    if (parseInt(captchaInput) !== captcha.answer) {
      setCaptchaError('Invalid CAPTCHA. Please try again.');
      generateCaptcha();
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage({ type: '', text: '' });

    // EmailJS configuration
    const serviceID = 'service_wcjbo9s';
    const templateID = 'template_ryc1m5h';
    const publicKey = 'iHm6E7nONO3REvkq5';

    // Prepare email template parameters
    // Note: Update your EmailJS template to use these variables:
    // {{name}}, {{time}}, {{message}}, {{email}}, {{phone}}
    const templateParams = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone || 'Not provided',
      message: formData.message,
      time: new Date().toLocaleString('en-IN', { 
        dateStyle: 'medium', 
        timeStyle: 'short' 
      }),
      title: `Contact Form Submission from ${formData.name}`,
    };

    try {
      // Send email using EmailJS
      await emailjs.send(serviceID, templateID, templateParams, publicKey);
      
      // Success
      setSubmitMessage({ 
        type: 'success', 
        text: 'Thank you for your message! We will get back to you soon.' 
      });
      setFormData({ name: '', email: '', phone: '', message: '' });
      setCaptchaInput('');
      generateCaptcha();
    } catch (error) {
      console.error('Email sending failed:', error);
      setSubmitMessage({ 
        type: 'error', 
        text: 'Failed to send message. Please try again or contact us directly.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // WhatsApp handler
  const handleWhatsAppClick = () => {
    const phoneNumber = '919609844403'; // Replace with your WhatsApp number (without +)
    const message = `Hello SARKAR TRADERS,%0A%0AI am interested in your products and would like to know more information.%0A%0APlease contact me.`;
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <>
      <Helmet>
        <title>Contact Us - SARKAR TRADERS</title>
        <meta name="description" content="Get in touch with SARKAR TRADERS. Contact us for inquiries, product information, or business partnerships." />
      </Helmet>
      
      <div className="contact-page">
        <div className="container">
          <h1>Contact Us</h1>
          
          <div className="contact-content">
            <div className="contact-info">
              <h2>Get in Touch</h2>
              <p>We'd love to hear from you. Send us a message and we'll respond as soon as possible.</p>
              
              <div className="contact-details">
                <div className="contact-item">
                  <FaPhone className="contact-icon" />
                  <div>
                    <h3>Phone</h3>
                    <a href="tel:+919609844403">+91 9609844403/+91 7001097483</a>
                  </div>
                </div>
                
                <div className="contact-item">
                  <FaEnvelope className="contact-icon" />
                  <div>
                    <h3>Email</h3>
                    <a href="mailto:sarkarnewton52@gmail.com">sarkarnewton52@gmail.com</a>
                  </div>
                </div>
                
                <div className="contact-item">
                  <FaMapMarkerAlt className="contact-icon" />
                  <div>
                    <h3>Address</h3>
                    <p>Domkal, Murshidabad, West Bengal</p>
                  </div>
                </div>
                
                <div className="whatsapp-contact">
                  <button onClick={handleWhatsAppClick} className="whatsapp-btn">
                    <FaWhatsapp className="whatsapp-icon" />
                    <span>Chat on WhatsApp</span>
                  </button>
                </div>
              </div>
            </div>
            
            <div className="contact-form-container">
              <h2>Send us a Message</h2>
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="phone">Phone</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    rows="5"
                    value={formData.message}
                    onChange={handleChange}
                    required
                  ></textarea>
                </div>
                
                {/* CAPTCHA */}
                <div className="form-group captcha-group">
                  <label htmlFor="captcha">
                    Security Check: {captcha.num1} + {captcha.num2} = ?
                  </label>
                  <div className="captcha-container">
                    <input
                      type="number"
                      id="captcha"
                      name="captcha"
                      value={captchaInput}
                      onChange={handleCaptchaChange}
                      placeholder="Enter answer"
                      className={captchaError ? 'captcha-input error' : 'captcha-input'}
                      required
                    />
                    <button
                      type="button"
                      onClick={generateCaptcha}
                      className="refresh-captcha-btn"
                      title="Refresh CAPTCHA"
                    >
                      🔄
                    </button>
                  </div>
                  {captchaError && <span className="captcha-error">{captchaError}</span>}
                </div>
                
                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
                
                {submitMessage.text && (
                  <div className={`submit-message ${submitMessage.type}`}>
                    {submitMessage.text}
                  </div>
                )}
              </form>
            </div>
          </div>
          
          {/* Map Section */}
          {/* To get your actual Google Maps embed URL:
              1. Go to https://www.google.com/maps
              2. Search for your exact company address (Domkal, Murshidabad, West Bengal)
              3. Click on "Share" button
              4. Select "Embed a map" tab
              5. Copy the iframe src URL and replace the src below with your actual embed URL
          */}
          <div className="map-section">
            <h2>Find Us on Map</h2>
            <div className="map-container">
              <iframe
                title="SARKAR TRADERS Location"
                src="https://www.google.com/maps?q=Domkal,+Murshidabad,+West+Bengal&output=embed"
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
              <div className="map-link-container">
                <a 
                  href="https://maps.apple/p/vnjLbFohvQIINm" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="apple-maps-link"
                >
                  Open in Apple Maps →
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;

